import { useEffect, useRef, useState } from 'react';
import { SearchX } from 'lucide-react';
import type { ReactNode } from 'react';
import type { EventSearchResults, EventSummary } from '../api/events';
import { TextField } from './Field';

/**
 * Finding an event by typing at it — TROOP-214.
 *
 * **One component, both doors.** `UnitHomeScreen` and `FamilyScreen` already share `EventAgenda`,
 * `MonthBar` and `EventMonthSection`, and rule 13b is that `/family` mirrors `/units` in routes and
 * never in rules — so a lookalike here would be two answers to what a search does. Only the *row*
 * and the *fetch* differ, which is what the two props are for.
 *
 * ## Where it lives, and where it must not
 *
 * On the **all-events view** — the calendar behind *View all* — because that is where somebody
 * looking for history already is, and because keeping it off the landing page is the whole point of
 * the split TROOP-213 made. Pointedly **not in the header**: TROOP-120 cut that bar to three icons
 * and an account menu after it needed 551px at a 390px viewport, and recorded that a fourth icon is
 * the regression. A search icon is the single most likely thing to be put there next.
 *
 * ## The interaction is `RosterPicker`'s; the mechanism is not
 *
 * The two-character minimum, the nothing-until-you-type, the empty state that explains rather than
 * showing an empty box — all copied on purpose, so somebody who has learnt one control has learnt
 * the other. What is **not** copied is how it gets its rows: `RosterPicker` fetches the whole
 * roster once and filters in the browser, which is right for ninety people and would mean shipping
 * a decade of a unit's events to the client to filter. This asks the server, per query.
 *
 * ## What the empty state has to do
 *
 * Say what was searched. Names and themes are matched and descriptions are not (see `EventSearch`
 * on the server for that decision), so a remembered phrase from a description finds nothing — and
 * an empty box with no explanation reads as a unit that never held the event. Kinds are not
 * searched either, which is a filter's job and TROOP-33's ticket; when the query names one, the
 * server says so and this names it back rather than sitting silently empty.
 */
export function EventSearch({
  search,
  renderItem,
}: {
  /** The door's own fetch. `/units/` and `/family` differ here and nowhere else. */
  search: (query: string) => Promise<EventSearchResults>;
  renderItem: (event: EventSummary) => ReactNode;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<EventSearchResults | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Which query is the current one.
   *
   * Two replies to two keystrokes can land in either order, and the later one is not necessarily
   * the newer one — so a slow answer to "kl" would otherwise overwrite the answer to "klondike"
   * and the box would show results for something nobody typed. Every reply checks it is still the
   * one being waited for.
   */
  const asked = useRef(0);

  /**
   * The fetch, held where the effect below cannot see it change.
   *
   * Both callers pass an inline arrow, so `search` is a new function on every render — and an
   * effect that depends on it would re-run on every render, set state, and re-render, which is a
   * loop that queries the server as fast as it can answer. Only what was *typed* should start a
   * search.
   */
  const latest = useRef(search);
  useEffect(() => {
    latest.current = search;
  }, [search]);

  const typed = query.trim();
  const enough = typed.length >= MINIMUM;

  useEffect(() => {
    if (!enough) {
      // Nothing is asked below the minimum -- not asked and discarded. The server refuses it too,
      // so this is about not making the request rather than about trusting the client.
      setResults(null);
      setBusy(false);
      setError(null);
      return;
    }

    const mine = ++asked.current;

    // Typing is faster than a round trip, so without this every keystroke is a query over the
    // unit's whole history. Short enough that it still feels like the list is following along.
    const timer = setTimeout(() => {
      setBusy(true);

      latest.current(typed)
        .then((found) => {
          if (asked.current !== mine) return;
          setResults(found);
          setError(null);
        })
        .catch((e) => {
          if (asked.current !== mine) return;
          setResults(null);
          setError(e instanceof Error ? e.message : 'Could not search for that.');
        })
        .finally(() => {
          if (asked.current === mine) setBusy(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [typed, enough]);

  const found = (results?.upcoming.length ?? 0) + (results?.past.length ?? 0);

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
      <TextField
        label="Search events"
        search
        type="search"
        value={query}
        // Never trimmed in the handler: these boxes are controlled, so trimming here eats the
        // trailing space before the next character arrives and the space bar appears dead.
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Klondike, camporee, court of honour"
        autoComplete="off"
        // What is searched, said before anybody types -- so a phrase remembered from a description
        // finding nothing is explained rather than surprising.
        hint="Event names and what each one was about. Past and still to come."
      />

      {error && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
          {error}
        </p>
      )}

      {typed.length > 0 && !enough && (
        <Note>Two characters starts a search.</Note>
      )}

      {enough && busy && !results && <Note>Searching&hellip;</Note>}

      {enough && results && found === 0 && !busy && (
        <div>
          <EmptyResult query={results.query} matchedKind={results.matchedKind} />
        </div>
      )}

      {enough && results && found > 0 && (
        <div className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
          {/* Still to come first, because a match that has not happened yet is the one somebody
              can still act on -- the same reasoning that puts Just finished below upcoming on the
              agenda. Absent rather than empty when nothing matches ahead: an empty group under a
              heading would be a box saying "no". */}
          {results.upcoming.length > 0 && (
            <Group
              label="Still to come"
              count={results.upcoming.length}
              more={results.moreUpcoming}
              renderItem={renderItem}
              items={results.upcoming}
            />
          )}

          {results.past.length > 0 && (
            <Group
              label="Already happened"
              count={results.past.length}
              more={results.morePast}
              renderItem={renderItem}
              items={results.past}
              // The answer to "when did we last run it" is row one, so say which way this reads.
              note="Most recent first"
            />
          )}
        </div>
      )}
    </div>
  );
}

function Group({
  label,
  count,
  more,
  note,
  items,
  renderItem,
}: {
  label: string;
  count: number;
  more: boolean;
  note?: string;
  items: EventSummary[];
  renderItem: (event: EventSummary) => ReactNode;
}) {
  return (
    <section>
      <div style={{ margin: '0 0 var(--space-3)' }}>
        <h3 className="t-label" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          {label}
          <span className="tabular" style={{ fontWeight: 400 }}>
            {' '}
            &middot; {count}
          </span>
        </h3>
        {note && (
          <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            {note}
          </p>
        )}
      </div>

      <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
        {items.map((event) => renderItem(event))}
      </div>

      {/* What was held back, said out loud. A list that stopped at twenty with nothing said would
          be the silent truncation TROOP-13 took out of the calendar, in a new place -- and here it
          would read as "that is all there ever was", which is the answer this feature exists to
          get right. */}
      {more && (
        <p
          className="t-caption"
          style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-3) 0 0' }}
        >
          More than these matched. Add another word to narrow it down.
        </p>
      )}
    </section>
  );
}

/**
 * Nothing matched — and the whole job here is to be a sentence rather than a blank.
 *
 * Two different nothings, because they need two different next moves: a query that names a kind of
 * event is somebody asking for a *filter*, and telling them the search does not do kinds is more
 * use than telling them nothing matched.
 */
function EmptyResult({ query, matchedKind }: { query: string; matchedKind: string | null }) {
  if (matchedKind) {
    return (
      <div
        style={{
          border: '1px dashed var(--color-border-strong)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-5)',
        }}
      >
        <p className="t-body" style={{ color: 'var(--color-text-primary)', margin: 0, maxWidth: '52ch' }}>
          <strong>{matchedKind}</strong> is a kind of event rather than the name of one, and kinds
          are not searched here. Try what this particular one was called, or the month it happened
          in on the calendar below.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px dashed var(--color-border-strong)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-5)',
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'flex-start',
      }}
    >
      <SearchX size={20} aria-hidden="true" style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
      <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '52ch' }}>
        Nothing is called <strong>{query}</strong>. Event names and what each one was about are
        searched &mdash; the longer description underneath an event is not.
      </p>
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
      {children}
    </p>
  );
}

/**
 * How much has to be typed before anything is asked for.
 *
 * Two, and the same two `RosterPicker` uses — a person who has learnt one of these controls should
 * not find the other behaving differently. The server holds the same number and refuses below it;
 * this one exists because the client has to decide **not to ask**, which is a decision no response
 * can carry.
 */
const MINIMUM = 2;

/**
 * How long the box waits after the last keystroke.
 *
 * Typing is faster than a round trip, so without this "klondike" is eight queries over a unit's
 * whole history. Short enough that the list still feels like it is following along.
 */
const DEBOUNCE_MS = 250;
